'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { BiRightArrowAlt, BiSolidCheckCircle, BiSolidStar } from 'react-icons/bi';
import { FaCircleChevronLeft, FaCircleChevronRight } from 'react-icons/fa6';
import { FaQuoteLeft } from 'react-icons/fa';
import { useTranslation } from '@/hooks/useTranslation';
import { SlSpeech } from 'react-icons/sl';
import CTABanner from '@/components/blocks/CTABanner';

type Props = { locale: string };

const Testimonial = ({ locale }: Props) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { t } = useTranslation(locale);

  const testimonials = [
    {
      id: 1,
      quote: t('Home/Testimonial/testimonial1/quote'),
      author: t('Home/Testimonial/testimonial1/author'),
      role: t('Home/Testimonial/testimonial1/role'),
      company: t('Home/Testimonial/testimonial1/company'),
      avatar: 'https://cdn.1cdn.app/application/LOUISEBOT/25062712362250_trustpilot.png',
      rating: 5,
      metrics: {
        improvement: '40%',
        label: t('Home/Testimonial/testimonial1/metric'),
      },
      verified: true,
      industry: t('Home/Testimonial/testimonial1/industry'),
    },
    {
      id: 2,
      quote: t('Home/Testimonial/testimonial2/quote'),
      author: t('Home/Testimonial/testimonial2/author'),
      role: t('Home/Testimonial/testimonial2/role'),
      company: t('Home/Testimonial/testimonial2/company'),
      avatar: 'https://cdn.1cdn.app/application/LOUISEBOT/2506271258183_Ecart_Logo.png',
      rating: 5,
      metrics: {
        improvement: '60%',
        label: t('Home/Testimonial/testimonial2/metric'),
      },
      verified: true,
      industry: t('Home/Testimonial/testimonial2/industry'),
    },
    {
      id: 3,
      quote: t('Home/Testimonial/testimonial3/quote'),
      author: t('Home/Testimonial/testimonial3/author'),
      role: t('Home/Testimonial/testimonial3/role'),
      company: t('Home/Testimonial/testimonial3/company'),
      avatar: 'https://cdn.1cdn.app/application/LOUISEBOT/25062713011741_woodyah.png',
      rating: 5,
      metrics: {
        improvement: '90%',
        label: t('Home/Testimonial/testimonial3/metric'),
      },
      verified: true,
      industry: t('Home/Testimonial/testimonial3/industry'),
    },
    {
      id: 4,
      quote: t('Home/Testimonial/testimonial4/quote'),
      author: t('Home/Testimonial/testimonial4/author'),
      role: t('Home/Testimonial/testimonial4/role'),
      company: t('Home/Testimonial/testimonial4/company'),
      avatar: 'https://cdn.1cdn.app/application/LOUISEBOT/25062712362250_trustpilot.png',
      rating: 5,
      metrics: {
        improvement: '40%',
        label: t('Home/Testimonial/testimonial4/metric'),
      },
      verified: true,
      industry: t('Home/Testimonial/testimonial4/industry'),
    },
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentData = testimonials[currentTestimonial];

  return (
    <section className="tw-relative tw-py-16 lg:tw-py-24 tw-overflow-hidden tw-bg-transparent>">
      {/* Background Effects */}
      <div className="tw-absolute tw-inset-0">
        {/* Decorative quotes */}
        <div className="tw-absolute tw-top-20 tw-left-10 tw-text-blue-200/30 tw-text-6xl tw-font-serif">&#34;</div>
        <div className="tw-absolute tw-bottom-20 tw-right-10 tw-text-purple-200/30 tw-text-6xl tw-font-serif tw-rotate-180">
          &#34;
        </div>
      </div>

      <div className="tw-relative tw-z-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <SlSpeech className="tw-w-4 tw-h-4" />
            {t('Home/Testimonial/badge')}
          </div>

          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend']">
            {t('Home/Testimonial/title')}
          </h2>

          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-font-['Lexend']">
            {t('Home/Testimonial/subtitle')}
          </p>
        </div>

        {/* Main Testimonial Card */}
        <div className="tw-max-w-5xl tw-mx-auto">
          <div className="tw-relative tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-border tw-border-gray-100 tw-overflow-hidden">
            {/* Background Pattern */}
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-50/50 tw-to-purple-50/50"></div>

            {/* Content */}
            <div className="tw-relative tw-z-10 tw-p-8 lg:tw-p-12">
              <div className="tw-grid lg:tw-grid-cols-3 tw-gap-8 tw-items-center">
                {/* Left: Quote and Content */}
                <div className="lg:tw-col-span-2 tw-space-y-6">
                  {/* Quote Icon */}
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-12 tw-h-12 tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                      <FaQuoteLeft className="tw-w-6 tw-h-6 tw-text-white" />
                    </div>

                    {/* Rating */}
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-flex">
                        {[...Array(currentData.rating)].map((_, i) => (
                          <BiSolidStar key={i} className="tw-w-5 tw-h-5 tw-text-yellow-400" />
                        ))}
                      </div>
                      <span className="tw-text-gray-600 tw-font-semibold tw-font-['Lexend']">
                        {currentData.rating}.0
                      </span>
                    </div>
                  </div>

                  {/* Quote Text */}
                  <blockquote className="tw-text-base tw-text-gray-800 tw-leading-relaxed tw-font-['Lexend'] tw-font-medium">
                    &#34;{currentData.quote}&#34;
                  </blockquote>

                  {/* Metrics */}
                  <div className="tw-inline-flex tw-items-center tw-gap-3 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-full tw-px-4 tw-py-2">
                    <div className="tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full"></div>
                    <span className="tw-text-green-700 tw-font-semibold tw-font-['Lexend']">
                      {currentData.metrics.improvement} {currentData.metrics.label}
                    </span>
                  </div>
                </div>

                {/* Right: Author Info */}
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-space-y-4">
                  {/* Avatar */}
                  <div className="tw-relative">
                    <Image
                      src={currentData.avatar}
                      alt={`${currentData.author} - ${currentData.role}`}
                      width={120}
                      height={120}
                      className="tw-w-24 tw-h-24 lg:tw-w-32 lg:tw-h-32 tw-rounded-full tw-object-cover tw-border-4 tw-border-white tw-shadow-lg"
                    />
                    {currentData.verified && (
                      <div className="tw-absolute tw--bottom-2 tw--right-2 tw-bg-blue-600 tw-rounded-full tw-p-1">
                        <BiSolidCheckCircle className="tw-w-6 tw-h-6 tw-text-white" />
                      </div>
                    )}
                  </div>

                  {/* Author Details */}
                  <div className="tw-space-y-2">
                    <div className="tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-text-lg">
                      {currentData.author}
                    </div>
                    <div className="tw-text-gray-600 tw-font-['Lexend']">{currentData.role}</div>
                    <div className="tw-text-blue-600 tw-font-semibold tw-font-['Lexend'] tw-text-sm">
                      {currentData.company}
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-['Lexend']">{currentData.industry}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="tw-flex tw-justify-center tw-items-center tw-gap-4 tw-mt-8">
          <button
            onClick={prevTestimonial}
            className="tw-w-12 tw-h-12 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-scale-105"
            aria-label="Previous testimonial"
          >
            <FaCircleChevronLeft className="tw-w-5 tw-h-5 tw-text-gray-600" />
          </button>

          {/* Dots */}
          <div className="tw-flex tw-gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`tw-w-3 tw-h-3 tw-rounded-full tw-transition-all tw-duration-300 ${
                  index === currentTestimonial ? 'tw-bg-blue-600 tw-w-8' : 'tw-bg-gray-300 hover:tw-bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextTestimonial}
            className="tw-w-12 tw-h-12 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-scale-105"
            aria-label="Next testimonial"
          >
            <FaCircleChevronRight className="tw-w-5 tw-h-5 tw-text-gray-600" />
          </button>
        </div>

        {/* CTA Section */}
        <CTABanner
          title={t('Home/Testimonial/cta-title')}
          description={t('Home/Testimonial/cta-description')}
          cta={t('Home/Testimonial/cta-button')}
          ctaTarget="register"
          locale={locale}
        />
      </div>

      <style jsx>{`
        .tw-animation-delay-1000 {
          animation-delay: 1000ms;
        }
      `}</style>
    </section>
  );
};

export default Testimonial;
